import {
  ChangeDetectorRef,
  Component,
  Input,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { AbstractControl, FormGroup, ValidationErrors } from '@angular/forms';
import { Subscription } from 'rxjs';
import { IQuestionnaireQuestion } from 'src/app/interfaces/Questionnaire/IQuestionnaire';

// Titles separate the instruction with "||" or "‖".
const TUG_TITLE_SEPARATOR = /\|\||‖/;

// The time in seconds is the score, and lower is better. 851 (assistive
// device) is free text and not scored.
const TUG_TIME = 'question850';

// A time above 0 with a dot and up to two decimals, e.g. "12.34". When the
// patient could not do the test the time stays empty: never 0 or text, which
// the backend reads as Incomplete.
const TUG_TIME_PATTERN = /^\d+(\.\d{1,2})?$/;

function timeValidator(control: AbstractControl): ValidationErrors | null {
  const value = String(control.value ?? '').trim();
  return !value || (TUG_TIME_PATTERN.test(value) && Number(value) > 0)
    ? null
    : { time: true };
}

@Component({
  selector: 'app-tug-questions',
  templateUrl: './tug-questions.component.html',
  styleUrl: './tug-questions.component.css',
  standalone: false,
})
export class TugQuestionsComponent implements OnInit, OnDestroy {
  @Input() elements: IQuestionnaireQuestion[] = [];
  // The page title holds the test's heading and instruction.
  @Input() pageTitle = '';
  @Input() formGroup!: FormGroup;

  private sub?: Subscription;

  constructor(private readonly cdr: ChangeDetectorRef) {}

  ngOnInit(): void {
    const time = this.formGroup.get(TUG_TIME);
    time?.addValidators(timeValidator);
    time?.updateValueAndValidity({ emitEvent: false });

    this.sub = this.formGroup.valueChanges.subscribe(() => {
      this.keepTimeAsNumber();
    });
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
  }

  getQuestionTitle(title?: string): string {
    return title?.split(TUG_TITLE_SEPARATOR)[0]?.trim() ?? '';
  }

  getQuestionInstruction(title?: string): string {
    return title?.split(TUG_TITLE_SEPARATOR)[1]?.trim() ?? '';
  }

  isTimeElement(element: IQuestionnaireQuestion): boolean {
    return element.name === TUG_TIME;
  }

  isEditable(element: IQuestionnaireQuestion): boolean {
    return !!this.formGroup.get(element.name)?.enabled;
  }

  isInvalid(element: IQuestionnaireQuestion): boolean {
    return !!this.formGroup.get(element.name)?.invalid;
  }

  // Only a time can be typed or pasted: digits, one dot and two decimals.
  // Greek keyboards type "12,34"; the backend reads only "12.34".
  private keepTimeAsNumber(): void {
    const time = this.formGroup.get(TUG_TIME);
    if (!time || time.disabled) return;

    const value = String(time.value ?? '');
    const [whole, ...decimals] = value
      .replace(/,/g, '.')
      .replace(/[^\d.]/g, '')
      .split('.');
    const seconds = whole.replace(/^0+(?=\d)/, '');
    const allowed = decimals.length
      ? `${seconds || '0'}.${decimals.join('').slice(0, 2)}`
      : seconds;

    if (allowed !== value) {
      time.setValue(allowed, { emitEvent: false });
      this.cdr.detectChanges();
    }
  }
}
